/**
 * userSettingsPosHardware.helpers.ts — Helpers puros del panel «POS, gaveta y
 * seguridad» (UserSettingsPosHardware y sus secciones extraídas).
 *
 * QUÉ EXPORTA: stableSerializePosPrinter, nativeErrorMessage,
 * buildPosBrandOptions, buildPosTransportOptions, buildPosLabelModeOptions,
 * POS_HARDWARE_SECTION_LABEL, POS_WEB_PRINT_GUIDE_URL
 *
 * QUÉ NO DEBE HACER: renderizar JSX, tocar almacenamiento ni estado.
 */

import { isDesktop, isNative } from "@/lib/platform";
import type {
  LabelPrinterMode,
  PosPrinterConfig,
} from "@/lib/native/posPrinterConfig";
import {
  POS_CONNECTION_TYPE_LABELS,
  POS_PRINTER_BRANDS,
} from "../constants/posPrinterCatalog";

export const POS_HARDWARE_SECTION_LABEL =
  "text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-slate-500 mb-2 px-0.5";

/**
 * Guía con video para configurar la impresión en el navegador web (relay/WebUSB).
 * Punto único a editar cuando se publique el video definitivo.
 */
export const POS_WEB_PRINT_GUIDE_URL =
  "https://altenot.com/guias/impresora-web";

/** Serializa los campos «duros» que disparan estado dirty (no incluye flags de UI). */
export function stableSerializePosPrinter(c: PosPrinterConfig): string {
  return JSON.stringify({
    t: c.transport,
    p: c.paperWidthMm,
    b: c.brand,
    n: c.deviceName,
    h: c.networkHost.trim(),
    port: c.networkPort,
    relay: c.networkRelayUrl?.trim() ?? "",
    m: c.bluetoothMac.trim().toUpperCase(),
    u: c.usbDeviceKey.trim(),
    pr: c.printReceipts,
    ap: c.autoPrintOnSale,
    mr: c.manualReceiptOnSale,
    lm: c.labelPrinterMode,
    lh: c.labelPrinter.networkHost.trim(),
    lport: c.labelPrinter.networkPort,
    lrelay: c.labelPrinter.networkRelayUrl?.trim() ?? "",
    lm2: c.labelPrinter.bluetoothMac.trim().toUpperCase(),
    lu: c.labelPrinter.usbDeviceKey.trim(),
    lp: c.labelPrinter.paperWidthMm,
    lt: c.labelPrinter.transport,
  });
}

/** Mensaje legible de un error nativo (patrón repetido en los adaptadores). */
export function nativeErrorMessage(e: unknown, fallback: string): string {
  return e && typeof e === "object" && "message" in e
    ? String((e as { message?: string }).message)
    : fallback;
}

export function buildPosBrandOptions(): { value: string; label: string }[] {
  return POS_PRINTER_BRANDS.map((b) => ({
    value: b.value,
    label: b.label,
  }));
}

export function buildPosTransportOptions(): {
  value: string;
  label: string;
  hint: string;
}[] {
  const conn = POS_CONNECTION_TYPE_LABELS;
  return [
    {
      value: "none",
      label: "Sin conectar",
      hint: "La impresora queda inactiva",
    },
    {
      value: "network",
      label: conn.network.label,
      hint: conn.network.description,
    },
    {
      value: "bluetooth_spp",
      label: conn.bluetooth_spp.label,
      // Desktop: BT emparejado = puerto COM virtual en Windows
      hint: isDesktop
        ? "Puerto COM de Windows para impresora Bluetooth emparejada (ej. COM3)."
        : conn.bluetooth_spp.description,
    },
    {
      value: "usb",
      label: conn.usb.label,
      hint: isDesktop
        ? "Puerto de sistema Windows para impresora USB (ej. USB001)."
        : conn.usb.description,
    },
  ];
}

/** Opciones del destino de etiquetas; en APK solo hay una impresora. */
export function buildPosLabelModeOptions(): {
  value: string;
  label: string;
  hint: string;
}[] {
  return [
    {
      value: "same",
      label: "Usar la impresora de recibos",
      hint: "Imprime la etiqueta en la térmica actual (con rollo adhesivo)",
    },
    ...(!isNative
      ? [
          {
            value: "separate",
            label: "Impresora separada de etiquetas",
            hint: "Una segunda térmica solo para etiquetas",
          },
        ]
      : []),
    {
      value: "off",
      label: "Desactivar etiquetas",
      hint: "Oculta el botón «Imprimir etiqueta» en el modal de peso",
    },
  ];
}

export type { LabelPrinterMode };
